import { createSlice, nanoid } from '@reduxjs/toolkit'

// The one copilot every surface in this app can actually continue: the bubble and
// the full page both send to the recruitment endpoint (WI-018, the interim
// default). It lives here, next to the state both surfaces share, because the
// bubble and the page disagreeing about it is a real defect and not a
// hypothetical: the bubble used to restore the newest conversation of ANY agent,
// including an HR one the page's sidebar cannot list, and typing into that thread
// rewrote its stored agent key and converted it.
export const RECRUITMENT_AGENT_KEY = 'recruitment_partner';
export const ELI_AGENT_KEY = 'eli';
// Eli's turns are filed under `eli`; the conversations that predate the router
// are still filed under `recruitment_partner` and are deliberately not rewritten,
// because they really were recruitment's. The sidebar asks for both.
export const BUBBLE_AGENT_KEYS = `${ELI_AGENT_KEY},${RECRUITMENT_AGENT_KEY}`;

const generateConversationId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `conv-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const greetingResults = () => ([
    {
        type: 'agent',
        data: {
            message: "Hi! I'm Eli, your intelligent HR assistant. I can help you navigate HyvidOne, explain features, generate reports, or take you to any section. Just ask me where you want to go or what you need help with!",
            timestamp: new Date(),
        }
    }
]);

const initialState = {
    copilotData: {
        requesting: false,
        error: null,
        success: false,
        message: '',
        results: null
    },
    chatData: {
        requesting: false,
        error: null,
        success: false,
        message: '',
        results: greetingResults(),
        conversationId: null,
        // Whether the transcript has been fetched for this session. Lives in redux,
        // NOT in a component ref, because ChatEli unmounts when the Eli page hides
        // the bubble, and a ref would reset on remount and silently reload the
        // newest conversation over whatever the user had opened.
        historyLoaded: false,
    },
    conversationsData: {
        requesting: false,
        error: null,
        results: [],
        offset: 0,
        hasMore: false,
    },
  };

export const copilotSlice = createSlice({
  name: 'copilot',
  initialState,
  reducers: {
    doAskCopilot: (state, action) => {
        state.copilotData.requesting = true;
        state.copilotData.error = null;
        state.copilotData.success = false;
        state.copilotData.message = action.payload;
    },
    doAskCopilotSuccess: (state, action) => {
        console.log(action.payload);
        state.copilotData.requesting = false;        
        state.copilotData.results = action.payload;
        state.copilotData.success = true; 
    },
    doAskCopilotFail: (state, action) => {        
        state.copilotData.requesting = false;
        state.copilotData.error = action.payload;
    },
    doAgentChat: (state, action) => {
        if (!state.chatData.conversationId) {
            state.chatData.conversationId = generateConversationId();
        }
        action.payload.conversationId = state.chatData.conversationId;
        state.chatData.requesting = true;
        state.chatData.error = null;
        state.chatData.success = false;
        state.chatData.message = action.payload;
        state.chatData.results.push({
            type: 'user',
            data: {
                ...action.payload,
                timestamp: new Date(),
            }
        });
        // FINDING (whole-branch review): sending a message is itself proof that
        // this session's transcript is whatever is on screen. Without this, a
        // user who lands straight on the Eli page and only ever types (never
        // opens a history row) leaves historyLoaded false, and the bubble
        // fetches the newest stored thread the moment it mounts, replacing the
        // conversation they were in the middle of.
        state.chatData.historyLoaded = true;
    },
    doAgentChatSuccess: (state, action) => {
        console.log(action.payload);
        state.chatData.requesting = false;    
        state.chatData.results.push({
            type: 'agent',
            data: {
                ...action.payload,
                timestamp: new Date(),
            }
        });
        state.chatData.success = true; 
    },
    doAgentChatFail: (state, action) => {
        state.chatData.requesting = false;
        state.chatData.error = action.payload;
        state.chatData.results.push({
            type: 'error',
            data: {
                ...action.payload,
                timestamp: new Date(),
            }
        });
    },
    // FIX C1: do NOT push a bubble here. Pushing unconditionally left a
    // permanent empty {message:''} bubble on screen whenever the stream fell
    // back to the non-streaming path, since that path never removed it. The
    // non-streaming fallback is gone as of item V: see failAgentStreaming
    // below. The bubble is now
    // created lazily, on the first token, by appendAgentToken below.
    startAgentStreaming: (state) => {
        state.chatData.requesting = true;
    },
    appendAgentToken: (state, action) => {
        const last = state.chatData.results[state.chatData.results.length - 1];
        // Only append onto a bubble that is still in-progress (data.streaming).
        // A prior, already-completed agent bubble must NOT be reused.
        if (last && last.type === 'agent' && last.data.streaming) {
            last.data.message += action.payload;
        } else {
            state.chatData.results.push({
                type: 'agent',
                data: {
                    message: action.payload,
                    timestamp: new Date(),
                    streaming: true,
                }
            });
        }
    },
    // Attaches a download offer to the bubble that is CURRENTLY streaming, using the
    // same selector appendAgentToken uses. An already-finished bubble must not be
    // reused, or an old reply would sprout a button belonging to a newer one.
    attachAgentArtifact: (state, action) => {
        const last = state.chatData.results[state.chatData.results.length - 1];
        if (last && last.type === 'agent' && last.data.streaming) {
            last.data.artifact = action.payload;
        }
    },
    finishAgentStreaming: (state) => {
        state.chatData.requesting = false;
        const last = state.chatData.results[state.chatData.results.length - 1];
        if (last && last.type === 'agent' && last.data.streaming) {
            last.data.streaming = false;
        }
    },
    // A stream that failed. Nothing here calls another agent.
    //
    // BUG-004 was the saga silently re-sending the message to chat-hr, so a user
    // entitled only to recruitment got an answer built from leave, payroll and
    // attendance with no permission re-check. The rule that replaced it: a stream
    // failure never produces another agent's answer. It holds for the top-level
    // router too, which is why it is written as a rule and not as a different
    // endpoint to call.
    //
    // Partial text already on screen is KEPT: the user should see what arrived.
    // Its `streaming` flag must still be cleared or showCopy in ChatMessageView
    // suppresses the copy button on that bubble forever.
    failAgentStreaming: (state, action) => {
        state.chatData.requesting = false;
        const last = state.chatData.results[state.chatData.results.length - 1];
        if (last && last.type === 'agent' && last.data.streaming) {
            last.data.streaming = false;
        }
        state.chatData.results.push({
            type: 'error',
            data: {
                // A user who ignores a failed turn and sends another that also
                // fails ends up with two error bubbles on screen at once (nothing
                // here or in doAgentChat pops a stale one). The id is what lets
                // doRetryAgentChat below tell them apart instead of guessing by
                // position.
                id: nanoid(),
                message: 'Something went wrong and Eli did not finish this reply. Try again.',
                retry: action.payload ?? null,
                timestamp: new Date(),
            }
        });
    },
    // Re-send after a failure. Deliberately NOT doAgentChat: that reducer pushes
    // the user's message into results, so reusing it would paint the question a
    // second time as though the user had retyped it.
    //
    // FINDING (whole-branch review): removal used to be positional (pop the last
    // item if it is an error). Two failed turns can both be on screen at once, so
    // retrying the OLDER one popped the NEWER one instead, destroying its retry
    // payload and leaving the older, already-superseded error bubble on screen
    // with a live button. Removal is now by id: the prepare callback below stows
    // the id of the bubble that was actually clicked in action.meta.errorId, and
    // the reducer removes that specific item wherever it sits.
    //
    // Do NOT fall back to a positional pop (or to removing whatever is last) when
    // the id does not match anything in results. A mismatch means we do not know
    // which bubble was clicked, and deleting the wrong one is the exact defect
    // this id exists to fix. Removing nothing is the safe failure.
    //
    // The saga's worker (copilot/saga.jsx, doAgentChat) reads only action.payload
    // for the request body, so wrapping it in { payload, meta } here does not
    // change what the saga sends.
    //
    // conversationId is deliberately NOT refreshed here: the id already inside the
    // stored payload is the conversation this turn belongs to. It cannot go stale,
    // because doNewChat replaces the whole results array and takes the error bubble
    // (and its retry payload) with it.
    doRetryAgentChat: {
        reducer: (state, action) => {
            const errorId = action.meta?.errorId;
            const at = state.chatData.results.findIndex(
                (r) => r.type === 'error' && r.data.id === errorId
            );
            if (at !== -1) {
                state.chatData.results.splice(at, 1);
            }
            state.chatData.requesting = true;
            state.chatData.error = null;
            state.chatData.success = false;
            state.chatData.message = action.payload;
        },
        prepare: (payload, errorId) => ({ payload, meta: { errorId } }),
    },
    doNewChat: (state) => {
        state.chatData.requesting = false;
        state.chatData.error = null;
        state.chatData.success = false;
        state.chatData.message = '';
        state.chatData.results = greetingResults();
        state.chatData.conversationId = generateConversationId();
        // FINDING (whole-branch review): the empty conversation this just created
        // IS what the session should be showing, so the bubble must not go and
        // fetch the newest stored thread over the top of it.
        //
        // Setting this TRUE does not contradict the standing ruling that doNewChat
        // must never set it back to FALSE. False is what causes the refetch that
        // undoes the new chat; true is what prevents it.
        state.chatData.historyLoaded = true;
    },
    doLoadChatHistory: (state) => {
        state.chatData.requesting = true;
        state.chatData.error = null;
        state.chatData.success = false;
    },
    doLoadChatHistorySuccess: (state, action) => {
        state.chatData.requesting = false;
        state.chatData.success = true;
        const messages = action.payload?.messages;
        if (messages && messages.length > 0) {
            state.chatData.results = messages.map((m) => ({
                type: m.role === 'user' ? 'user' : 'agent',
                data: {
                    message: m.message,
                    timestamp: m.timestamp,
                    // Rehydrated from aim_payload, so refreshing the page keeps the
                    // download button instead of costing two more Bedrock calls.
                    artifact: m.artifact ?? null,
                }
            }));
            state.chatData.conversationId = action.payload.conversation_id;
        } else {
            state.chatData.results = greetingResults();
            state.chatData.conversationId = generateConversationId();
        }
        state.chatData.historyLoaded = true;
    },
    doLoadChatHistoryFail: (state, action) => {
        state.chatData.requesting = false;
        state.chatData.error = action.payload;
    },
    doLoadConversations: (state) => {
        state.conversationsData.requesting = true;
        state.conversationsData.error = null;
    },
    doLoadConversationsSuccess: (state, action) => {
        state.conversationsData.requesting = false;
        const page = action.payload?.conversations ?? [];
        const limit = action.payload?.limit ?? 50;
        const offset = action.payload?.offset ?? 0;
        // offset 0 is a fresh load and replaces; anything else is Load older and appends.
        state.conversationsData.results = offset === 0 ? page : [...state.conversationsData.results, ...page];
        state.conversationsData.offset = offset;
        // A full page is the only signal we have that more may exist. A short page
        // proves there are none, which is what hides the Load older control.
        state.conversationsData.hasMore = page.length === limit;
    },
    doLoadConversationsFail: (state, action) => {
        state.conversationsData.requesting = false;
        state.conversationsData.error = action.payload;
    },
    doOpenConversation: (state) => {
        // Deliberately does NOT set chatData.requesting. That flag renders
        // "Eli is thinking..." in the transcript, which is a lie here: we are
        // fetching an old transcript, not waiting on the model. The previous
        // transcript stays on screen until the new one arrives, which is also
        // what every other chat product does.
        state.chatData.error = null;
    },
    doOpenConversationSuccess: (state, action) => {
        state.chatData.requesting = false;
        state.chatData.success = true;
        const messages = action.payload?.messages ?? [];
        state.chatData.results = messages.map((m) => ({
            type: m.role === 'user' ? 'user' : 'agent',
            data: {
                message: m.message,
                timestamp: m.timestamp,
                artifact: m.artifact ?? null,
            },
        }));
        // Deliberately NOT greetingResults() plus a new id when the list is empty,
        // which is what doLoadChatHistorySuccess does. This action opens a
        // conversation the user picked from the sidebar; replacing it with a fresh
        // one would discard the selection and leave that sidebar row pointing at
        // nothing.
        state.chatData.conversationId = action.payload?.conversation_id ?? state.chatData.conversationId;
        state.chatData.historyLoaded = true;
    },
    doOpenConversationFail: (state, action) => {
        state.chatData.requesting = false;
        state.chatData.error = action.payload;
    },
  },
})

export const {
    doAskCopilot, doAskCopilotSuccess, doAskCopilotFail,
    doAgentChat, doAgentChatSuccess, doAgentChatFail,
    startAgentStreaming, appendAgentToken, attachAgentArtifact, finishAgentStreaming,
    failAgentStreaming, doRetryAgentChat,
    doNewChat, doLoadChatHistory, doLoadChatHistorySuccess, doLoadChatHistoryFail,
    doLoadConversations, doLoadConversationsSuccess, doLoadConversationsFail,
    doOpenConversation, doOpenConversationSuccess, doOpenConversationFail,
} = copilotSlice.actions;

export default copilotSlice.reducer;
